import { JaaSMeeting, JitsiMeeting } from '@jitsi/react-sdk';
import React, { useRef, useState } from 'react';

const JitsiMeetingView = ({ userName = 'Participant', roomName = 'meeting-room-123', onMeetingEnd }) => {
    const apiRef = useRef();
    const [ logItems, updateLog ] = useState([]);
    const [ showNew, toggleShowNew ] = useState(false);
    const [ knockingParticipants, updateKnockingParticipants ] = useState([]);

    const printEventOutput = payload => {
        updateLog(items => [ ...items, JSON.stringify(payload) ]);
    };

    const handleAudioStatusChange = (payload, feature) => {
        if (payload.muted) {
            updateLog(items => [ ...items, `${feature} off` ])
        } else {
            updateLog(items => [ ...items, `${feature} on` ])
        }
    };

    const handleChatUpdates = payload => {
        if (payload.isOpen || !payload.unreadCount) {
            return;
        }
        apiRef.current.executeCommand('toggleChat');
        updateLog(items => [ ...items, `you have ${payload.unreadCount} unread messages` ])
    };

    const handleKnockingParticipant = payload => {
        updateLog(items => [ ...items, JSON.stringify(payload) ]);
        updateKnockingParticipants(participants => [ ...participants, payload?.participant ])
    };

    const resolveKnockingParticipants = condition => {
        knockingParticipants.forEach(participant => {
            apiRef.current.executeCommand('answerKnockingParticipant', participant?.id, condition(participant));
            updateKnockingParticipants(participants => participants.filter(item => item.id === participant.id));
        });
    };

    const handleJitsiIFrameRef1 = iframeRef => {
        iframeRef.style.border = 'none';
        iframeRef.style.background = '#000';
        iframeRef.style.width = '100vw';
        iframeRef.style.height = '100vh';
        iframeRef.style.position = 'fixed';
        iframeRef.style.top = '0';
        iframeRef.style.left = '0';
        iframeRef.style.zIndex = '9999';
        iframeRef.style.margin = '0';
        iframeRef.style.padding = '0';
        // iframeRef.contentDocument.querySelector('.watermark').style.display = 'none';
    };

    const handleJitsiIFrameRef2 = iframeRef => {
        iframeRef.style.marginTop = '10px';
        iframeRef.style.border = '10px dashed #df486f';
        iframeRef.style.padding = '5px';
        iframeRef.style.height = '400px';
    };

    const handleJaaSIFrameRef = iframeRef => {
        iframeRef.style.border = '10px solid #3d3d3d';
        iframeRef.style.background = '#3d3d3d';
        iframeRef.style.height = '400px';
        iframeRef.style.marginBottom = '20px';
    };

    const handleApiReady = apiObj => {
        apiRef.current = apiObj;
        apiRef.current.on('knockingParticipant', handleKnockingParticipant);
        apiRef.current.on('audioMuteStatusChanged', payload => handleAudioStatusChange(payload, 'audio'));
        apiRef.current.on('videoMuteStatusChanged', payload => handleAudioStatusChange(payload, 'video'));
        apiRef.current.on('raiseHandUpdated', printEventOutput);
        apiRef.current.on('titleViewChanged', printEventOutput);
        apiRef.current.on('chatUpdated', handleChatUpdates);
        apiRef.current.on('knockingParticipant', handleKnockingParticipant);
        
        // Listen for meeting end events
        apiRef.current.on('videoConferenceLeft', handleMeetingEnd);
        apiRef.current.on('readyToClose', handleMeetingEnd);
    };

    const handleMeetingEnd = (event) => {
        console.log('Meeting ended:', event);
        onMeetingEnd(event);
    };
    
    const handleReadyToClose = () => {
        // This is called when Jitsi is ready to close
        handleMeetingEnd({ reason: 'readyToClose' });
    };

    const generateRoomName = () => `JitsiMeetRoomNo${Math.random() * 100}-${Date.now()}`;

    // Multiple instances demo
    const renderNewInstance = () => {
        if (!showNew) {
            return null;
        }

        return (
            <JitsiMeeting
                roomName = { generateRoomName() }
                getIFrameRef = { handleJitsiIFrameRef2 } />
        );
    };

    const renderButtons = () => (
        <div style = {{ margin: '15px 0' }}>
            <div style = {{
                display: 'flex',
                justifyContent: 'center'
            }}>
                <button
                    type = 'text'
                    title = 'Click to execute toggle raise hand command'
                    style = {{
                        border: 0,
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: '#f8ae1a',
                        color: '#040404',
                        padding: '12px 46px',
                        margin: '2px 2px'
                    }}
                    onClick = { () => apiRef.current.executeCommand('toggleRaiseHand') }>
                    Raise hand
                </button>
                <button
                    type = 'text'
                    title = 'Click to approve/reject knocking participant'
                    style = {{
                        border: 0,
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: '#0056E0',
                        color: 'white',
                        padding: '12px 46px',
                        margin: '2px 2px'
                    }}
                    onClick = { () => resolveKnockingParticipants(({ name }) => !name.includes('test')) }>
                    Resolve lobby
                </button>
                <button
                    type = 'text'
                    title = 'Click to execute subject command'
                    style = {{
                        border: 0,
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: '#df486f',
                        color: 'white',
                        padding: '12px 46px',
                        margin: '2px 2px'
                    }}
                    onClick = { () => apiRef.current.executeCommand('subject', 'New Subject')}>
                    Change subject
                </button>
                <button
                    type = 'text'
                    title = 'Click to create a new JitsiMeeting instance'
                    style = {{
                        border: 0,
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: '#3D3D3D',
                        color: 'white',
                        padding: '12px 46px',
                        margin: '2px 2px'
                    }}
                    onClick = { () => toggleShowNew(!showNew) }>
                    Toggle new instance
                </button>
            </div>
        </div>
    );

    const renderLog = () => logItems.map(
        (item, index) => (
            <div
                style = {{
                    fontFamily: 'monospace',
                    padding: '5px'
                }}
                key = { index }>
                {item}
            </div>
        )
    );

    const renderSpinner = () => (
        <div style = {{
            fontFamily: 'sans-serif',
            textAlign: 'center'
        }}>
            Loading..
        </div>
    );


    return (
        <>
            {/* <h1 style = {{
                fontFamily: 'sans-serif',
                textAlign: 'center'
            }}>
                JitsiMeeting Demo App
            </h1> */}
            <JitsiMeeting
                domain = 'jitsi-dev.phx-app.com'
                roomName = { roomName }
                spinner = { renderSpinner }
                configOverwrite = {{
                    startWithAudioMuted: true,
                    startWithVideoMuted: true,
                    enableWelcomePage: false,
                    prejoinPageEnabled: false,          // Disable prejoin screen
                    prejoinConfig: {
                        enabled: false                   // Force disable prejoin
                    },
                    disableDeepLinking: true,            // Prevent native app redirects
                    requireDisplayName: false,           // Don't require name input
                    enableInsecureRoomNameWarning: false, // Skip room name warnings
                    disableProfile: false,               // Allow profile but don't force it
                    
                    // Mobile webview specific configurations
                    enableNoAudioDetection: false,       // Disable audio detection warnings
                    enableNoisyMicDetection: false,      // Disable noisy mic detection
                    disableAudioLevels: false,           // Keep audio levels enabled
                    
                    // Ensure webview compatibility
                    testing: {
                        mobileXmppWsThreshold: 0         // Force WebSocket for mobile
                    },
                    
                    // Participant/Audience mode - disable moderator capabilities
                    disableRemoteMute: true,              // Cannot mute others
                    disableKick: true,                    // Cannot kick participants
                    disableModeratorIndicator: false,     // Show who is moderator
                    disableReactionsModeration: true,     // Cannot moderate reactions
                    
                    // Toolbar configuration - Zoom-like bottom positioning
                    toolbarConfig: {
                        alwaysVisible: false,
                        autoHideWhileChatIsOpen: false,
                        initialTimeout: 4000,
                        timeout: 4000
                    },
                    
                    // Toolbar buttons - Show only chat and leave (hangup)
                    // Note: Other buttons will be accessible via the overflow menu (three dots)
                    toolbarButtons: [
                        'chat',
                        'settings',
                        'hangup'
                        // All other buttons will appear in the overflow menu (three dots)
                    ],
                    
                    // Filmstrip configuration - Completely hide participant video thumbnails
                    filmstrip: {
                        disableResizable: true,
                        disableStageFilmstrip: true,
                        disabled: true
                    },
                    
                    // Disable filmstrip to hide participant videos
                    disableFilmstripAutohiding: true,
                    
                    // Disable self view and remote thumbnails
                    disable1On1Mode: false,
                    
                    // Video quality settings
                    resolution: 720,
                    constraints: {
                        video: {
                            height: {
                                ideal: 720,
                                max: 1080,
                                min: 360
                            }
                        }
                    },
                    
                    // Notifications
                    disableJoinLeaveSounds: false,
                    
                    // UI optimizations
                    hideConferenceSubject: true,
                    hideConferenceTimer: false,
                    hideParticipantsStats: false,
                    
                    // Disable unnecessary features for cleaner UI
                    disableInviteFunctions: false,
                    doNotStoreRoom: false,
                    
                    // Disable tile view to show only moderator (stage view)
                    startWithTileView: false,
                    disableTileView: true,
                    
                    // Disable self view
                    disableSelfView: true,
                    
                    // Background settings
                    backgroundAlpha: 0.5,
                    
                    // Reactions
                    disableReactions: false,
                    
                    // Recording - limited for participants
                    fileRecordingsEnabled: false,         // Participants cannot record
                    liveStreamingEnabled: false,          // Participants cannot livestream
                    
                    // Transcription
                    transcribingEnabled: false,
                    
                    // Hide conference info (timer, subject, etc.)
                    conferenceInfo: {
                        alwaysVisible: [],
                        autoHide: []
                    },
                    
                    // Disable connection indicator
                    disableConnectionIndicator: true
                }}
                interfaceConfigOverwrite = {{
                    // Filmstrip settings - Completely hide participant video thumbnails
                    FILM_STRIP_MAX_HEIGHT: 0,
                    VERTICAL_FILMSTRIP: false,
                    
                    // Hide filmstrip completely
                    filmStripOnly: false,
                    DISABLE_RINGING: true,
                    DISABLE_FOCUS_INDICATOR: true,
                    
                    // Video layout - fit entire screen
                    VIDEO_LAYOUT_FIT: 'both',
                    TILE_VIEW_MAX_COLUMNS: 5,
                    
                    // Disable branding for cleaner look
                    SHOW_JITSI_WATERMARK: false,
                    SHOW_WATERMARK_FOR_GUESTS: false,
                    SHOW_BRAND_WATERMARK: false,
                    SHOW_POWERED_BY: false,
                    
                    // Hide Jitsi logo in top left corner
                    DEFAULT_LOGO_URL: 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
                    JITSI_WATERMARK_LINK: '',
                    
                    // Hide connection/performance indicator
                    CONNECTION_INDICATOR_DISABLED: true,
                    
                    // Mobile optimizations - Critical for webview
                    MOBILE_APP_PROMO: false,
                    HIDE_DEEP_LINKING_LOGO: true,
                    NATIVE_APP_NAME: '',
                    APP_SCHEME: '',
                    
                    // Notifications
                    DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
                    DISABLE_PRESENCE_STATUS: false,
                    
                    // UI elements
                    DISABLE_DOMINANT_SPEAKER_INDICATOR: false,
                    DISABLE_TRANSCRIPTION_SUBTITLES: false,
                    DISABLE_VIDEO_BACKGROUND: false,
                    
                    // Settings sections available
                    SETTINGS_SECTIONS: ['devices', 'language', 'profile', 'sounds'],
                    
                    // Display settings
                    DEFAULT_BACKGROUND: '#000000',
                    DEFAULT_REMOTE_DISPLAY_NAME: 'Participant',
                    DEFAULT_LOCAL_DISPLAY_NAME: 'Me',
                    
                    // Optimal browsers
                    OPTIMAL_BROWSERS: ['chrome', 'chromium', 'firefox', 'electron', 'safari'],
                    
                    // Connection indicators
                    CONNECTION_INDICATOR_AUTO_HIDE_ENABLED: true,
                    CONNECTION_INDICATOR_AUTO_HIDE_TIMEOUT: 5000,
                    
                    // Recent list
                    RECENT_LIST_ENABLED: false,
                    
                    // Generate room names
                    GENERATE_ROOMNAMES_ON_WELCOME_PAGE: false,
                    
                    // Hide invite header when alone
                    HIDE_INVITE_MORE_HEADER: false
                }}
                userInfo = {{
                    displayName: userName
                }}
                lang = 'en'
                onApiReady = { externalApi => handleApiReady(externalApi) }
                onReadyToClose = { handleReadyToClose }
                getIFrameRef = { handleJitsiIFrameRef1 } />
            {/* <JaaSMeeting
                roomName = { generateRoomName() }

                // Update this with the `8x8.vc` or `stage.8x8.vc` version of interest
                // and avoid mixing up different domains and release versions
                // on the same page at the same time, as only the first
                // external api script will be loaded.
                // release = 'release-1234'

                useStaging = { true }
                getIFrameRef = { handleJaaSIFrameRef } /> */}
            {/* {renderButtons()}
            {renderNewInstance()}
            {renderLog()} */}
        </>
    );
};

export default JitsiMeetingView;